/**
 * FRONT-2598 — pure decisions behind the Canvas editor for a sketch or a deck.
 */
import { ApiError } from '~/lib/api-client';

/** The edit view the iframe loads (owner + flag only; src/artifacts/edit-view.ts). `v` busts the cache. */
export function editFrameSrc(slug: string, version: string): string {
  return `/apps/${encodeURIComponent(slug)}/?eddy-edit=1&v=${encodeURIComponent(version)}`;
}

/**
 * What a sketch or deck shows in the Canvas body. An item the person may edit shows the editor from
 * the moment the panel opens (FRONT-2743): the editor owns the draft, the Escape guard and the
 * save-before-send flush. An item that cannot be edited shows the plain page — unless it STOPPED
 * being editable while its draft is unsaved (Eddy rebuilt the deck, say): then the editor stays on
 * screen, locked, so the typed text is never silently thrown away; the person can still discard it
 * (or copy it out), and the panel says why saving is off.
 */
export type ArtifactSurface = 'editor' | 'frame';

export function artifactSurface(input: { editable: boolean; dirty: boolean }): ArtifactSurface {
  if (input.editable) return 'editor';
  return input.dirty ? 'editor' : 'frame';
}

export type ServerVersionAction = 'ignore' | 'reload' | 'conflict';

const time = (iso: string) => new Date(iso).getTime();

/**
 * The server now holds `observed` (from a refetch or an `artifact-updated`
 * event) while the iframe shows `snapshotAt`.
 *
 *  - Not newer than what is on screen, or our own save still in flight (its event
 *    can arrive before its response): nothing to do. After the save the snapshot
 *    IS the new version, so the same event is then "not newer" — that equality is
 *    what keeps our own save from reloading the frame a second time.
 *  - Newer and the draft is clean: reload.
 *  - Newer and the draft is dirty: never reload (it would eat the person's typing);
 *    show the conflict notice instead.
 */
export function planServerVersion(input: {
  observed: string | null;
  snapshotAt: string;
  saving: boolean;
  dirty: boolean;
}): ServerVersionAction {
  if (!input.observed || input.saving) return 'ignore';
  const o = time(input.observed);
  const s = time(input.snapshotAt);
  if (Number.isNaN(o) || Number.isNaN(s) || o <= s) return 'ignore';
  return input.dirty ? 'conflict' : 'reload';
}

export type SaveRefusal =
  | { kind: 'stale' }
  | { kind: 'unmatched'; message: string }
  | { kind: 'message'; message: string; deckBlocked?: true }
  | null;

/**
 * A 409 carrying `currentUpdatedAt` is a stale save (the artifact moved on). A 409 carrying
 * `editsUnknown: true` means some typed edit matched no block of the saved version: nothing was
 * written, and the editor treats it like a conflict so "Re-apply my edits" places them by their
 * original text instead of leaving the person at a dead end.
 * Any other refusal whose body is `{ error, userFacing: true }` was written by
 * the server for the person (a blocked deck, edits that no longer match, a size
 * limit, a bad slide list): its sentence is shown as it is. Anything else —
 * a network failure, a 5xx, a body that is not marked — is null, and the editor
 * says its own generic "Couldn't save".
 */
export function classifySaveRefusal(err: unknown): SaveRefusal {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { error?: unknown; userFacing?: unknown; currentUpdatedAt?: unknown; editsUnknown?: unknown; deckBlocked?: unknown } | null | undefined;
  if (err.status === 409 && typeof body?.currentUpdatedAt === 'string') return { kind: 'stale' };
  if (err.status === 409 && body?.editsUnknown === true && typeof body.error === 'string' && body.error.trim()) {
    return { kind: 'unmatched', message: body.error };
  }
  if (body?.userFacing === true && typeof body.error === 'string' && body.error.trim()) {
    return body.deckBlocked === true ? { kind: 'message', message: body.error, deckBlocked: true } : { kind: 'message', message: body.error };
  }
  return null;
}

export const ARTIFACT_CONFLICT_SAVE_REASON = 'Choose “Re-apply my edits” or “Load EDDY\'s version” first.';

/** How many times one load may be remounted because the framed page navigated itself, before the frame is locked. */
export const MAX_FRAME_REMOUNTS = 3;

/**
 * The framed page said hello a second time: it navigated (a meta refresh, a `location` change).
 * A remount keeps the draft and re-applies it; a page that does it again and again is not
 * editable here, so after `MAX_FRAME_REMOUNTS` the frame is locked instead of reloaded forever.
 */
export function planFrameNavigation(remountsSoFar: number): 'remount' | 'lock' {
  return remountsSoFar < MAX_FRAME_REMOUNTS ? 'remount' : 'lock';
}

export const frameKeepsReloadingReason = (kind: 'sketch' | 'deck') =>
  `This ${kind} keeps reloading itself, so it can't be edited here. Ask EDDY to change it in chat.`;

/** A frame the editor gave up on is reported to the panel (which then shows the plain page) only while the draft is clean; a dirty draft stays mounted, locked, so typed text is never thrown away. */
export const shouldReportFrameUnstable = (draftDirty: boolean): boolean => !draftDirty;
